import { ToastAction } from "@/components/ui/toast";
import type { useToast } from "@/hooks/use-toast";
import type { FriendlyAuthError } from "@/lib/authErrors";

type ToastFn = ReturnType<typeof useToast>["toast"];

type NavigateFn = (path: string) => void;

/**
 * Renders the enriched leaked-password toast: prose guidance + strong-password
 * examples, plus an optional "Go to reset password" action when the friendly
 * error carries a `resetCta` (only Settings' password-update context today).
 */
export function showLeakedPasswordToast(
  toast: ToastFn,
  friendly: FriendlyAuthError,
  opts?: { navigate?: NavigateFn },
) {
  const examples = friendly.examples ?? [];
  const navigate: NavigateFn =
    opts?.navigate ??
    ((path) => {
      if (typeof window !== "undefined") {
        window.location.href = path;
      }
    });

  const description = (
    <div className="space-y-2">
      <p>{friendly.description}</p>
      {examples.length > 0 && (
        <div>
          <p className="font-medium">Try one of these:</p>
          <ul className="list-disc pl-4 space-y-1">
            {examples.map((example) => (
              <li key={example}>{example}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const action = friendly.resetCta ? (
    <ToastAction
      altText={friendly.resetCta.label}
      onClick={() => navigate(friendly.resetCta!.path)}
    >
      {friendly.resetCta.label}
    </ToastAction>
  ) : undefined;

  toast({
    title: friendly.title,
    description,
    variant: "destructive",
    action,
  });
}
